import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from '@/config/dayjs';
import { Box, Typography } from '@mui/material';
import AsOfDatePicker from '@/components/date/AsOfDatePicker';
import WidgetHeader from '@/components/common/WidgetHeader';
import AccountsGrid from '@/modules/portfolio/grids/accountsGrid';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { WARootState } from '@/store/store';
import { useLocalization } from '@/hooks/useLocalization';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { BALANCE_DESC_GRID_SORT_MODEL } from '@/constants/grid';
import { ContentContainer } from '../contentContainer';

const MANAGED_ACCOUNTS_WIDGET_CODE = 'investor-accounts-aum';
const MANAGED_ACCOUNT_LIST_FIELDS = [
  'Name',
  'AccountNumber',
  'Balance',
  'formattedSubtype',
  'LastUpdated',
];

const ManagedAccountList = ({
  isWidget = false,
  title,
  onDataLoaded,
  container = 'widget',
}: StandardWidgetProps) => {
  const { t } = useLocalization();
  const [date, setDate] = useState(() => dayjs().format('MM/DD/YYYY'));
  const userInfo = useSelector((state: WARootState) => state.appView.UserInfo);

  const firmName =
    userInfo?.AGSettings?.find((setting) => setting.AdvisorGroupName)
      ?.AdvisorGroupName ||
    userInfo?.AppName ||
    t('firm', 'firm');

  const emptyStateMessage = useMemo(
    () => (
      <Typography>
        {t('managedAccountsEmptyState', {
          firmName,
          defaultValue:
            'No {{firmName}} accounts. Accounts connected through {{firmName}} will appear here automatically. Contact your advisor if you think something is missing.',
        })}
      </Typography>
    ),
    [firmName, t],
  );

  return (
    <ContentContainer
      sx={{
        '@media print': {
          paddingX: 0,
          paddingTop: 0,
          paddingBottom: 0,
          gap: 0,
        },
      }}
      container={container}
    >
      <WidgetHeader
        title={title || t('managedAccounts', 'Managed Accounts')}
        rightContent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <AsOfDatePicker
                date={date}
                onChange={setDate}
                label={t('asOf')}
              />
            </Box>
          </Box>
        }
      />

      <Box>
        <AccountsGrid
          isWidget={isWidget}
          endDate={date}
          groupBy='none'
          showHome={true}
          showAway={false}
          widgetCode={MANAGED_ACCOUNTS_WIDGET_CODE}
          allowedFields={MANAGED_ACCOUNT_LIST_FIELDS}
          defaultSortModel={BALANCE_DESC_GRID_SORT_MODEL}
          emptyStateMessage={emptyStateMessage}
          onDataLoaded={onDataLoaded}
        />
      </Box>
    </ContentContainer>
  );
};

export default ManagedAccountList;
